import posthog from 'posthog-js';

const sensitiveTypes = ['password', 'email', 'tel'];

const sensitiveNames = [
  'pin',
  'ssn',
  'routing',
  'cardnumber',
  'cvv',
  'accountnumber',
  'account',
  'accountid',
  'password',
  'oldpassword',
  'newpassword',
  'confirmpassword',
  'firstname',
  'lastname',
  'zipcode',
  'zip',
  'address',
  'street',
];

const maskInputFn = (text: string, element?: HTMLElement): string => {
  const type = element?.getAttribute('type')?.toLowerCase() ?? '';
  const name = (
    element?.getAttribute('name') ??
    element?.getAttribute('id') ??
    ''
  ).toLowerCase();

  if (
    sensitiveTypes.includes(type) ||
    sensitiveNames.some(s => name.includes(s))
  ) {
    return '*'.repeat(text.length);
  }

  return '*'.repeat(text.length);
};

const getPostHogKey = (): string | undefined => {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  if (typeof key !== 'string' || !key.trim()) return undefined;
  // Reject placeholder/literal values that must not be used as keys
  if (/^YOUR_/i.test(key) || /^#\{/.test(key) || /^(placeholder|undefined|null)$/i.test(key)) return undefined;
  return key.trim();
};

export const initPostHog = (): void => {
  const key = getPostHogKey();
  if (!key) return;
  posthog.init(key, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    defaults: '2026-01-30',
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',

    // Explicitly enable session recording — never rely on the default.
    // Without this, `defaults: '2026-01-30'` or future SDK updates could
    // silently change the behaviour.
    disable_session_recording: false,

    session_recording: {
      // All <input>, <textarea>, and <select> values are masked by default.
      // maskInputFn provides extra defense-in-depth for known sensitive field
      // types/names so they remain masked even when type is toggled to "text"
      // (e.g. "show password" buttons).
      maskAllInputs: true,
      maskInputFn,

      // Any element with data-ph-mask="true" will have its displayed text
      // masked in session replays.
      //
      // Usage in JSX:
      //   <span data-ph-mask="true">{accountNumber}</span>
      //
      // Use this for sensitive values rendered outside of form inputs, such as
      // displayed account numbers, loan balances, or PII shown on screen.
      maskTextSelector: '[data-ph-mask="true"]',

      // Elements with the class "ph-no-capture" are completely removed from
      // session recordings. The replay shows an empty placeholder in their place.
      //
      // Usage in JSX:
      //   <div className="ph-no-capture">...</div>
      //
      // Use this for third-party widgets, iframes, or entire sections that must
      // never appear in any replay under any circumstance.
    },

    loaded: instance => {
      if (import.meta.env.DEV) {
        console.debug(
          '[PostHog] Initialized. Session recording active:',
          instance.sessionRecordingStarted(),
        );
      }
    },
  });
};
